import React, { ReactNode, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useScrollLock } from "@/hooks/useScollLock";

/**
 * 학생 FE 모달의 공통 a11y(접근성, accessibility) 베이스.
 * `modal/`·`mypage-v2/modals`·`teacher`·학습 화면 등 20곳 넘게 이 위에 올라가 있다(`grep BaseDialog`).
 * 여기 동작(닫힘 조건·포커스)을 바꾸면 그 전부가 같이 바뀐다.
 *
 * * design/accessibility.md 모달 체크리스트 전항목 충족:
 *  - role="dialog" + aria-modal="true" + aria-labelledby (title 있을 때) 또는 aria-label (headerless)
 *  - 포커스 트랩(Tab/Shift+Tab 순환, 열림 시 첫 포커스 가능 요소로 이동, 닫힘 시 트리거 복귀)
 *  - ESC 키로 닫힘
 *  - 배경 클릭으로 닫힘. 단 누르기(pointerdown)와 떼기(pointerup) 둘 다 배경에서 일어났을 때만이다.
 *    한쪽이 다이얼로그 안이면 브라우저는 click 을 두 지점의 공통 조상(=배경)에 보내므로 click 의
 *    target 만 봐서는 드래그와 클릭을 구분할 수 없다. 입력 칸에서 글자를 긁다 밖에서 떼는 경우와
 *    배경에서 누르고 다이얼로그 안에서 떼는 경우 둘 다 폼이 통째로 닫혔다.
 *    `closeOnOverlayClick={false}` 로 끌 수 있다(opt-in). 긴 폼처럼 실수로 배경을 눌러 입력을
 *    잃으면 안 되는 모달용이며, 그때는 × 버튼·ESC 같은 명시적 닫기만 남는다.
 *  - body 스크롤 잠금 (useScrollLock 단일 출처)
 *
 * ★ `onClose` 는 ref 로 들고 있어 열림 효과(포커스 이동·키 구독)는 `isOpen` 이 바뀔 때만 돈다.
 *   호출부가 `onClose={() => ...}` 처럼 매 렌더 새 함수를 넘기면(대부분 그렇다) 효과가 부모
 *   렌더마다 다시 돌면서 cleanup 이 포커스를 트리거로 돌려보내고, 다시 첫 포커스 요소(×)로
 *   옮겼다. 학습 화면은 감사 로그 큐(Recoil) 갱신으로 자주 렌더되므로 폼에 글자를 치다가
 *   포커스를 × 버튼에 뺏겼다 — 붙여넣기(Ctrl+V)가 × 에 떨어져 "붙여넣기가 안 되는" 증상이었다.
 *
 * 렌더 위치: 항상 document.body 로 포털된다. 호출부가 `position: fixed` 인 조상
 * (예: 문제 풀이 화면 하단 버튼 바 `.problem-button-footer`) 안에 있으면 그 조상이
 * 쌓임 맥락(stacking context)을 만들어, 모달이 아무리 z-index 를 올려도 조상보다
 * z 가 높은 형제(`.problem-header` z-50)에 덮인다. 포털이 그 결합을 끊는다.
 *
 * 모드:
 *  - 기본(헤더): title prop 제공 시 상단 제목 + 닫기 버튼(×) 자동 렌더
 *  - headerless: title 미제공 시 헤더 미렌더, children 안에서 자유 구성 + ariaLabel 필수
 */
interface BaseDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** 헤더 제목. 미제공 시 headerless 모드. */
  title?: string;
  /** headerless 모드에서 스크린리더에 다이얼로그 정체성을 알리는 라벨. title 없을 때 필수. */
  ariaLabel?: string;
  /** 다이얼로그 본문(children). */
  children: ReactNode;
  /** 최대 너비. 기본 440px. */
  widthClassName?: string;
  /** 헤더 모드에서 닫기 버튼(×) 노출 여부. 기본 true. */
  showCloseButton?: boolean;
  /** 다이얼로그 컨테이너 추가 클래스(padding/rounded 등 override). */
  containerClassName?: string;
  /** 배경(오버레이) 클래스 override. 모바일 풀스크린처럼 기본 여백(py-10 px-4)을 없애야 할 때만. */
  overlayClassName?: string;
  /** 배경 클릭으로 닫을지. 기본 true. false 면 × 버튼·ESC 로만 닫힌다(긴 폼의 입력 유실 방지). */
  closeOnOverlayClick?: boolean;
}

/** 실제 다이얼로그 컴포넌트. props 의미는 위 BaseDialogProps 주석 참고. */
export default function BaseDialog({
  isOpen,
  onClose,
  title,
  ariaLabel,
  children,
  widthClassName = "w-[440px] max-w-[calc(100vw-32px)]",
  showCloseButton = true,
  containerClassName = "bg-white rounded-[20px] p-6 mt-20",
  overlayClassName = "fixed inset-0 z-[100] flex items-start justify-center bg-inverse/40 overflow-y-auto py-10 px-4",
  closeOnOverlayClick = true,
}: BaseDialogProps) {
  const titleId = useId();
  // 포털 대상(document.body)은 클라이언트에만 있다. SSR/prerender 에서는 아무것도 그리지 않는다.
  const [mounted, setMounted] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const pressedOnOverlayRef = useRef(false);
  const releasedOnOverlayRef = useRef(false);

  useScrollLock(isOpen);

  useEffect(() => setMounted(true), []);

  // dev assertion: headerless 모드일 때 ariaLabel 필수 (스크린리더 식별 보장)
  if (process.env.NODE_ENV !== "production" && isOpen && !title && !ariaLabel) {
    // eslint-disable-next-line no-console
    console.warn(
      "BaseDialog: title 또는 ariaLabel 중 하나는 필수입니다(스크린리더 식별)."
    );
  }

  useEffect(() => {
    if (!isOpen) return;
    // 트리거 캡처
    triggerRef.current = document.activeElement as HTMLElement;
    // 첫 포커스. data-autofocus 우선, 없으면 첫 포커스 가능 요소
    const focusable =
      dialogRef.current?.querySelector<HTMLElement>(
        "[data-autofocus], button, input, textarea, select, [tabindex]:not([tabindex='-1'])"
      );
    focusable?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onCloseRef.current();
        return;
      }
      if (e.key !== "Tab") return;
      // 포커스 트랩. Tab/Shift+Tab 순환
      const root = dialogRef.current;
      if (!root) return;
      const focusables = root.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);

    return () => {
      document.removeEventListener("keydown", onKey);
      // 포커스 트리거 복귀
      triggerRef.current?.focus?.();
    };
  }, [isOpen]);

  const onOverlayPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    pressedOnOverlayRef.current = e.target === e.currentTarget;
    releasedOnOverlayRef.current = false;
  };
  const onOverlayPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    releasedOnOverlayRef.current = e.target === e.currentTarget;
  };
  // pointerdown → pointerup → click 순서라 click 시점에는 두 ref 가 모두 채워져 있다.
  const onOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const pressed = pressedOnOverlayRef.current;
    const released = releasedOnOverlayRef.current;
    pressedOnOverlayRef.current = false;
    releasedOnOverlayRef.current = false;
    if (!closeOnOverlayClick) return;
    if (e.target !== e.currentTarget || !pressed || !released) return;
    onClose();
  };

  if (!isOpen || !mounted) return null;

  const labelProps = title
    ? { "aria-labelledby": titleId }
    : { "aria-label": ariaLabel ?? "다이얼로그" };

  return createPortal(
    <div
      className={overlayClassName}
      onPointerDown={onOverlayPointerDown}
      onPointerUp={onOverlayPointerUp}
      onClick={onOverlayClick}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        {...labelProps}
        className={`${containerClassName} ${widthClassName}`}
        onClick={(e) => e.stopPropagation()}
      >
        {title ? (
          <div className="flex items-start justify-between mb-4">
            <h2 id={titleId} className="text-18 text-heading font-bold font-heading">
              {title}
            </h2>
            {showCloseButton ? (
              <button
                type="button"
                onClick={onClose}
                aria-label="닫기"
                data-autofocus
                className="w-[32px] h-[32px] rounded-full bg-page text-strong hover:bg-fill"
              >
                ×
              </button>
            ) : null}
          </div>
        ) : null}
        {children}
      </div>
    </div>,
    document.body
  );
}
